import {
  Box, Typography, useMediaQuery, useTheme, IconButton, Grid, Button,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Grid2 from "@mui/material/Grid2";
import { useDispatch, useSelector } from "react-redux";
import ExpandLessTwoToneIcon from "@mui/icons-material/ExpandLessTwoTone";
import ExpandMoreTwoToneIcon from "@mui/icons-material/ExpandMoreTwoTone";
import { format_Number } from "../../utils/utils";
import CardSection from "./CardSection";
import { setSelectedCombinationExtraProduct } from "../../store/slice/Addguest.slice";

const IMG_URL = process.env.REACT_APP_API_LOCAL_IMG_URL;

// Reusable product card to avoid duplicating before/after sections
const ProductCard = ({
  item, selectedUpsaleCombination, qty, Css, languageWords,
  handleProductSelect, handleQuantityChange,
}) => {
  const theme = useTheme();
  const [isImageValid, setIsImageValid] = useState(true);
  const isSelected = selectedUpsaleCombination?.some((p) => p.idproduct === item.idproduct);

  return (
    <Box
      onClick={(e) => handleProductSelect(e, item)}
      sx={{
        border: "1px solid #EBEBEB",
        p: { xs: 1, sm: 1.5, md: 2 },       
        borderRadius: "10px",
        backgroundColor: isSelected ? Css?.upsaleComHighlightBgClr : Css?.upsaleComBgClr,
        mb: { xs: 1, sm: 2 },
        boxShadow: "rgba(0, 0, 0, 0.1) 2px 2px 8px",
        width: "100%", cursor: "pointer", transition: "all 0.3s ease",
        "&:hover": { boxShadow: "rgba(0, 0, 0, 0.2) 4px 4px 12px" },
      }}
    >
      <Grid container spacing={1} alignItems="center">
        {item?.imageFileName && isImageValid && (
          <Grid item xs={12} sm={4} md={2}>
            <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "8px", overflow: "hidden" }}>
              <img
                src={`${IMG_URL}upload/product${item?.imageFileName?.replace(/\\/g, "/")}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onLoad={() => setIsImageValid(true)} onError={() => setIsImageValid(false)}
              />
            </Box>
          </Grid>
        )}

        <Grid item xs={12} sm={item?.imageFileName && isImageValid ? 8 : 12} md={item?.imageFileName && isImageValid ? 10 : 12}>
          <Box sx={{ width: "100%" }}>

            {/* ── Title + Add Button ── */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{
                fontSize: { xs: "13px", sm: "14px", md: "1rem" }, 
                fontWeight: "bold",
                color: isSelected ? Css?.upsaleComHighlightBgClr : Css?.upsaleComItemHeadingClr,
                flex: 1, mr: 1,
              }}>
                {item?.productName}
              </Typography>

              <Button
                variant="outlined"
                onClick={(e) => handleProductSelect(e, item)}
                sx={{
                  position: "relative",
                  borderRadius: "10px",
                  padding: { xs: "4px 8px", sm: "6px 12px", md: "8px 16px" },
                  minWidth: { xs: "70px", sm: "90px", md: "120px" },           
                  borderColor: "#dcdcdc", textTransform: "none",
                  display: "flex", flexDirection: "column", lineHeight: 1.2, flexShrink: 0,
                  "&:hover": { borderColor: "#bdbdbd" },
                }}
              >
                <Typography sx={{
                  fontSize: { xs: "11px", sm: "12px", md: "14px" }, fontWeight: 500,
                  color: isSelected ? Css?.upsaleAddActiveTxtClr : Css?.upsaleAddTxtClr,
                }}>
                  {languageWords?.Add}
                </Typography>

                {item?.price > 0 && (
                  <Typography sx={{
                    fontSize: { xs: "11px", sm: "12px", md: "13px" }, fontWeight: 600,
                    color: isSelected ? Css?.upsaleAddActiveTxtClr : Css?.upsaleAddTxtClr,
                  }}>
                    €{format_Number(parseFloat(item?.price).toFixed(2))}
                  </Typography>
                )}

                {isSelected && (
                  <DeleteOutlineIcon sx={{
                    position: "absolute", bottom: 4, right: 4,
                    fontSize: { xs: 14, sm: 16, md: 18 },
                    color: isSelected ? Css?.upsaleAddActiveTxtClr : Css?.upsaleAddTxtClr,
                    cursor: "pointer", opacity: 0.9, "&:hover": { opacity: 1 },
                  }} />
                )}
              </Button>
            </Box>

            {/* ── Description ── */}
            {item?.productinformation && (
              <Typography sx={{
                color: isSelected ? Css?.upsaleComHighlightItemInfoClr ?? "#fff" : Css?.upsaleComItemInfoClr,
                fontSize: { xs: "12px", sm: "13px", md: "14px" },
                mt: 0.5, mb: 1,
              }}>
                {item?.productinformation}
              </Typography>
            )}

            {/* ── Quantity Stepper ── */}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Box sx={{
                display: "flex", alignItems: "center",
                gap: { xs: "4px", sm: "8px" },
                px: { xs: 0.5, sm: 1 }, py: 0.5,
                borderRadius: "999px",
                backgroundColor: isSelected ? Css?.upsaleCounterHighlightBgClr : Css?.UpsaleCounterBtnBgClr,
                backdropFilter: "blur(6px)",
              }}>
                <Button
                  onClick={(e) => handleQuantityChange(e, item, false)}
                  disabled={(qty[item?.idproduct] || 1) === 1}
                  disableRipple
                  sx={{
                    minWidth: { xs: 28, sm: 32, md: 36 }, height: { xs: 28, sm: 32, md: 36 },
                    borderRadius: "50%", fontSize: { xs: "16px", sm: "18px", md: "20px" },
                    fontWeight: 600, color: isSelected ? Css?.upsaleCounterIconHighlightClr : Css?.upsaleCounterIconClr,
                    backgroundColor: "transparent", transition: "all 0.25s ease",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.08)", transform: "scale(1.05)" },
                    "&:active": { transform: "scale(0.95)" },
                  }}
                >−</Button>

                <Typography sx={{
                  minWidth: { xs: 20, sm: 24, md: 28 }, textAlign: "center",
                  fontSize: { xs: "12px", sm: "13px", md: "14px" }, fontWeight: 600,
                  color: isSelected ? "#fff" : "#424242", transition: "color 0.3s ease",
                }}>
                  {qty[item?.idproduct] || 1}
                </Typography>

                <Button
                  onClick={(e) => handleQuantityChange(e, item, true)}
                  disableRipple
                  sx={{
                    minWidth: { xs: 28, sm: 32, md: 36 }, height: { xs: 28, sm: 32, md: 36 },
                    borderRadius: "50%", fontSize: { xs: "16px", sm: "18px", md: "20px" },
                    fontWeight: 600, color: isSelected ? Css?.upsaleCounterIconHighlightClr : Css?.upsaleCounterIconClr,
                    backgroundColor: "transparent", transition: "all 0.25s ease",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.08)", transform: "scale(1.05)" },
                    "&:active": { transform: "scale(0.95)" },
                  }}
                >+</Button>
              </Box>
            </Box>

          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const UpsaleProductCombination = ({
  price, steps, activeStep, handleBack, handleNextFunc, errors, isLoading,
  languageId, total_product_price, combinationUpsale, selectedUpsaleCombination,
  UpsaleCombinationData, qty, setQty, setSelectedExtraProduct, totalCount,
  languageWords, reservType, selectedDate, adult, child, kid, baby, toddler,
  selectedExtraProduct, time, transactionDetails, selectedCombination,
  transactionPrice, excludingVat, totalVat, totalIncludingVAT, totalCostYourBooking, setUpsaleCombination,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [isShowMore, setIsShowMore] = useState(false);
  const { css } = useSelector((store) => store?.widgetSettings);
  const Css = css?.find((item) => item?.cssTabFieldsGroupName === "Upsale combination")?.fields;

  const beforeProducts = UpsaleCombinationData?.filter((item) => item?.isBefore);
  const afterProducts = UpsaleCombinationData?.filter((item) => !item?.isBefore);

  const handleProductSelect = (e, item) => {
    e.stopPropagation();
    const isSelected = selectedUpsaleCombination?.some((p) => p.idproduct === item.idproduct);
    const currentQuantity = qty[item.idproduct] || 0;
    const newQuantity = isSelected ? 0 : currentQuantity + 1;
    if (isSelected) {
      dispatch(setSelectedExtraProduct(selectedUpsaleCombination?.filter((p) => p.idproduct !== item.idproduct)));
    } else {
      dispatch(setSelectedExtraProduct([...selectedUpsaleCombination, { ...item, quantity: newQuantity }]));
    }
    dispatch(setQty({ ...qty, [item.idproduct]: newQuantity }));
  };

  const handleQuantityChange = (e, item, increment) => {
    e.stopPropagation();
    const currentQuantity = qty[item.idproduct] || 0;
    const newQuantity = Math.max(currentQuantity + (increment ? 1 : -1), 0);
    dispatch(setQty({ ...qty, [item.idproduct]: newQuantity }));
    if (newQuantity === 0) {
      dispatch(setSelectedExtraProduct(selectedUpsaleCombination.filter((p) => p.idproduct !== item.idproduct)));
    } else {
      const updated = selectedUpsaleCombination.map((p) =>
        p.idproduct === item.idproduct ? { ...p, quantity: newQuantity } : p
      );
      if (!updated.some((p) => p.idproduct === item.idproduct)) {
        updated.push({ ...item, quantity: newQuantity });
      }
      dispatch(setSelectedExtraProduct(updated));
    }
  };

  const sharedProps = { selectedUpsaleCombination, qty, Css, languageWords, handleProductSelect, handleQuantityChange };

  return (
    <Box>
      <Grid2 container spacing={2}>
        <Grid2 size={{ md: 9, sm: 12, xs: 12 }}>

          {/* ── Before Section ── */}
          {beforeProducts?.length > 0 && (
            <Typography sx={{
              fontSize: { xs: "13px", sm: "14px", md: "1rem" },
              fontWeight: "bold", mb: 1, mt: 1, ml: 1,
              color: Css?.upsaleCombinationHeadingClr,
            }}>
              {`${languageWords?.before || "Before"} ${reservType?.name}`}
            </Typography>
          )}
          {beforeProducts?.map((item, index) => (
            <ProductCard key={index} item={item} {...sharedProps} />
          ))}

          {/* ── After Section ── */}
          {afterProducts?.length > 0 && (
            <Typography sx={{
              fontSize: { xs: "13px", sm: "14px", md: "1rem" },
              fontWeight: "bold", mb: 1, mt: 1, ml: 1,
              color: Css?.upsaleCombinationHeadingClr,
            }}>
              {`${languageWords?.after || "After"} ${reservType?.name}`}
            </Typography>
          )}
          {afterProducts?.map((item, index) => (
            <ProductCard key={index} item={item} {...sharedProps} />
          ))}

          {UpsaleCombinationData?.length > 4 && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <IconButton
                sx={{
                  backgroundColor: theme?.product?.addBtnBg, fontSize: "14px",
                  color: theme?.product?.addBtnTextColor,
                  "&:hover": { backgroundColor: theme?.product?.addBtnBgHover, color: theme?.product?.addBtnTextHover },
                }}
                onClick={() => setIsShowMore(!isShowMore)}
              >
                {!isShowMore ? <ExpandMoreTwoToneIcon /> : <ExpandLessTwoToneIcon />}
              </IconButton>
            </Box>
          )}
        </Grid2>

        <Grid2 size={{ md: 3, sm: 12, xs: 12 }}>
          <CardSection
            price={price} steps={steps} activeStep={activeStep} handleBack={handleBack}
            handleNextFunc={handleNextFunc} errors={errors} isLoading={isLoading}
            languageId={languageId} totalCount={totalCount} languageWords={languageWords}
            reservType={reservType} selectedDate={selectedDate} adult={adult} child={child}
            kid={kid} baby={baby} toddler={toddler} selectedExtraProduct={selectedExtraProduct}
            time={time} transactionDetails={transactionDetails} selectedCombination={selectedCombination}
            transactionPrice={transactionPrice} excludingVat={excludingVat} totalVat={totalVat}
            totalIncludingVAT={totalIncludingVAT} totalCostYourBooking={totalCostYourBooking}
            combinationUpsale={combinationUpsale} total_product_price={total_product_price}
          />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default UpsaleProductCombination;